/**
 * Numéro 9 du programme pédagogique ETM — Les premiers secours.
 *
 * Thème ETM 'S'. Méthode PAS (Protéger, Alerter, Secourir).
 * Spécificités moto : retrait du casque, blessures fréquentes en chute.
 */

import { HeartPulse, AlertTriangle, Lightbulb, Quote, Users } from 'lucide-react';

export const premiersSecoursEtmIssue = {
  slug: 'premiers-secours',
  themeCode: 'S',
  issueNumber: 9,
  totalIssues: 9,
  category: 'Premiers secours',
  shortTitle: 'Premiers secours',
  title: 'Tu peux sauver une vie en 3 gestes',
  kicker: 'Numéro 9 du programme pédagogique ETM',
  subtitle:
    "Méthode PAS (Protéger, Alerter, Secourir) et spécificités moto : retrait du casque, position d'attente, hémorragies. Le numéro qui transforme un motard en sauveur.",
  heroImage: '/img/magazines/premiers-secours.jpg',
  heroImageAlt: 'Ambulance en intervention d’urgence sur une rue de ville, gyrophares allumés',
  readTimeMin: 13,

  seoDescription:
    "Premiers secours moto pour ETM 2026 : méthode PAS, retrait du casque, position latérale de sécurité, hémorragies. Lecture pédagogique + QCM final.",
  seoKeywords:
    "premiers secours moto, méthode PAS, retrait casque, ETM secours, PLS moto, accident moto secours",

  edito: {
    title: 'Pourquoi tu dois savoir ces gestes — même si tu ne les utiliseras jamais',
    body: "Les <strong>5 premières minutes</strong> après un accident déterminent 80 % du pronostic vital. Pourtant, 70 % des Français ne savent pas pratiquer les gestes qui sauvent. Ce numéro te donne en 13 minutes <strong>la méthode officielle PAS</strong> + les spécificités moto que beaucoup d'automobilistes ignorent. Le QCM final te dira si tu pourrais, demain, agir si tu arrives le premier sur un accident.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    // === CHAPITRE 1 — PAS ===
    {
      type: 'lesson',
      icon: HeartPulse,
      partLabel: 'Chapitre 1',
      title: 'La méthode officielle : P-A-S',
      paragraphs: [
        "PAS = <strong>Protéger, Alerter, Secourir</strong>. Cet ordre n'est PAS facultatif : si tu inverses, tu mets en danger toi-même, la victime et les autres usagers. C'est la base enseignée dans toutes les formations SST, PSC1, et c'est ce que l'ETM va te demander.",
        "<strong>1. PROTÉGER</strong> (priorité absolue, 30 secondes max). Tu sécurises la zone pour empêcher un suraccident. Étapes : (a) <strong>baliser</strong> à 50-100 m en amont avec triangle ou panneaux de signalisation, (b) faire <strong>ralentir/dévier</strong> les autres véhicules avec gilet jaune obligatoire, (c) <strong>NE JAMAIS</strong> déplacer la victime sauf si danger imminent (incendie, ravin, voie ferrée).",
        "<strong>2. ALERTER</strong> (1 à 2 minutes). Tu appelles le 112 (Europe), le 15 (SAMU), le 18 (pompiers) ou le 17 (police). Tu donnes : <strong>(a) ta localisation précise</strong> (km, route, ville la plus proche), <strong>(b) la nature de l'accident</strong> (moto-voiture, choc frontal, etc.), <strong>(c) le nombre et l'état des victimes</strong> (consciente ? respire ? saigne ?), <strong>(d) ton numéro de téléphone</strong>. <strong>Ne raccroche jamais en premier</strong> — l'opérateur peut avoir besoin de plus d'infos.",
        "<strong>3. SECOURIR</strong> (en attendant l'arrivée des secours). Tu pratiques les gestes adaptés selon l'état de la victime : parler si consciente, position latérale de sécurité (PLS) si inconsciente et respire, compression manuelle d'une hémorragie, etc. Tu ne quittes <strong>JAMAIS</strong> la victime jusqu'à l'arrivée des secours.",
      ],
      keyPoints: [
        'P-A-S = Protéger, Alerter, Secourir (dans CET ordre)',
        'Protéger d\'abord : baliser, gilet jaune, NE PAS déplacer la victime',
        'Alerter 112 (Europe), 15 (SAMU), 18 (pompiers)',
        'Ne JAMAIS raccrocher en premier',
      ],
    },

    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "Depuis 2012, <strong>tout véhicule motorisé doit transporter un gilet jaune haute visibilité et un triangle de présignalisation</strong>. À moto, le gilet est OBLIGATOIRE depuis 2016 (article R416-19) en cas d'arrêt d'urgence — sanction : 135 € + 1 point. Et au-delà de la sanction : sans gilet, tu es invisible la nuit ou par mauvais temps. Le triangle, lui, doit être placé à <strong>30 mètres minimum</strong> de l'accident, jamais sur autoroute (interdit pour ta sécurité).",
    },

    // === CHAPITRE 2 — Retrait du casque ===
    {
      type: 'lesson',
      icon: HeartPulse,
      partLabel: 'Chapitre 2',
      title: 'Le casque : retirer ou laisser ? La règle vitale',
      paragraphs: [
        "Voici le point ETM le plus piégeux. La règle officielle :",
        "<strong>Si la victime RESPIRE et est CONSCIENTE</strong> : on <strong>laisse le casque</strong>. On ouvre simplement la visière pour faciliter la respiration. Le retrait risque de déplacer une vertèbre cervicale et de causer une paralysie irréversible.",
        "<strong>Si la victime NE RESPIRE PAS ou si la respiration est impossible avec le casque</strong> (vomissements, sang dans la gorge, masque facial occlus) : on RETIRE le casque, à <strong>2 personnes IDÉALEMENT</strong>. Procédure : la personne 1 maintient la tête + nuque alignées dans l'axe corps, la personne 2 ouvre la jugulaire, écarte les attaches latérales et tire le casque doucement par traction axiale (vers l'arrière du crâne, sans rotation).",
        "Seul cas où tu retires seul : victime inconsciente et qui ne respire pas, et aucune autre personne n'est sur place. Et même là, tu peux attendre les secours qui arrivent en moins de 10 minutes en zone urbaine si la situation le permet (= si elle respire encore mal mais respire).",
        "<strong>RÈGLE D'OR ETM</strong> : par défaut, on NE retire JAMAIS le casque. On ouvre la visière, on parle à la victime, on attend les secours.",
      ],
      keyPoints: [
        'Par défaut : on NE RETIRE PAS le casque',
        'On ouvre la visière + on parle à la victime',
        'Retrait UNIQUEMENT si elle ne respire pas',
        'Retrait à 2 personnes idéalement (traction axiale)',
      ],
    },

    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« La victime est mal en point, je retire le casque pour qu'elle soit mieux. » <strong>DANGER MORTEL.</strong> Une lésion cervicale aggravée par un mauvais retrait peut transformer un blessé en tétraplégique à vie. C'est la cause numéro 1 de poursuites pour « blessures involontaires aggravées » contre des sauveteurs bien intentionnés. <strong>Retiens ce mantra</strong> : si elle respire et te parle, le casque RESTE. Tu lui sauves la nuque même sans le savoir.",
    },

    // === CHAPITRE 3 — PLS et hémorragies ===
    {
      type: 'lesson',
      icon: HeartPulse,
      partLabel: 'Chapitre 3',
      title: 'Inconsciente mais respire : la PLS sauve la vie',
      paragraphs: [
        "La <strong>position latérale de sécurité (PLS)</strong> empêche l'asphyxie par chute de la langue ou par vomissements quand la victime est inconsciente mais respire. C'est le geste le plus utile que tu apprendras pour l'ETM.",
        "Méthode rapide : (1) <strong>vérifier qu'elle respire</strong> en collant ton oreille à 5 cm de sa bouche pendant 10 secondes, (2) <strong>basculer doucement la victime sur le côté</strong>, jambe du dessus pliée à 90°, bras du dessus sous la joue paume vers le haut, (3) <strong>basculer la tête en arrière</strong> pour ouvrir les voies respiratoires, (4) <strong>surveiller la respiration toutes les minutes</strong> jusqu'à l'arrivée des secours.",
        "Si la victime <strong>ne respire pas du tout</strong> : commence immédiatement le massage cardiaque (30 compressions, 2 insufflations, alterner). Continue sans arrêt jusqu'à l'arrivée des secours ou la reprise d'une respiration. Compte fort à voix haute : <em>1-2-3-4… 30 !</em>",
        "Hémorragie externe abondante (sang qui jaillit ou imprègne les vêtements) : <strong>compression manuelle directe</strong> avec un linge propre, sans relâcher. Si ça ne suffit pas et qu'il n'y a pas d'objet enfoncé : <strong>garrot à 5 cm au-dessus de la plaie</strong>, serré jusqu'à arrêt du saignement, noter l'heure de pose.",
      ],
      keyPoints: [
        'Inconsciente + respire = position latérale de sécurité (PLS)',
        'Ne respire pas = massage cardiaque 30/2 sans interruption',
        'Hémorragie = compression manuelle directe puis garrot si nécessaire',
        'Garrot : noter l\'heure de pose, NE PAS le desserrer',
      ],
    },

    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Quand un de mes élèves me dit \"je n'oserai jamais intervenir\", je lui rappelle la <strong>loi du 9 avril 2004</strong> : la non-assistance à personne en danger est punie de <strong>5 ans de prison et 75 000 € d'amende</strong>. La loi te protège juridiquement de tes erreurs si tu agis de bonne foi. <strong>Ce qui est puni, c'est de NE RIEN faire.</strong> Donc le pire choix, c'est l'inaction. Même un appel au 112 sans rien d'autre est une assistance. Souviens-toi : tu n'as pas besoin d'être médecin pour sauver une vie. Tu as juste besoin de connaître PAS. »",
    },

    {
      type: 'story',
      icon: Users,
      title: 'Le témoignage : « 90 secondes ont suffi à le ramener »',
      body: "« J'arrivais sur un carrefour quand j'ai vu un motard au sol, immobile. Aucun témoin. J'étais paniqué mais je me suis souvenu de PAS. J'ai garé ma moto en travers pour bloquer la voie (Protéger), j'ai appelé le 18 en donnant le PK exact (Alerter), j'ai constaté qu'il respirait, j'ai laissé son casque, ouvert sa visière et je lui ai parlé en continu. Il s'est réveillé 90 secondes après. Les pompiers sont arrivés en 7 minutes. <strong>Le médecin m'a dit que mon réflexe \"je ne touche pas au casque\" avait probablement évité une tétraplégie</strong> car il avait une fracture cervicale C5. Je n'avais que 4 mois de permis. » <em>— Thomas, 21 ans</em>",
    },
  ],

  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '5 min',
    middle: 'C\'est le délai critique',
    label: 'pour intervenir : 80 % du pronostic vital se joue dans les 5 premières minutes',
    source: 'Croix-Rouge française + SAMU',
    accent: '#dc2626',
  },

  glossary: [
    { term: 'PAS', def: 'Protéger, Alerter, Secourir. Méthode officielle française enseignée dans toute formation aux premiers secours (PSC1, SST).' },
    { term: '112', def: 'Numéro d\'appel d\'urgence européen unique, gratuit. Redirige automatiquement vers le service le plus adapté (police, pompiers, SAMU).' },
    { term: '15', def: 'SAMU (Service d\'Aide Médicale Urgente). Pour les urgences médicales graves (arrêt cardiaque, hémorragie, malaise).' },
    { term: '18', def: 'Pompiers. Pour les accidents, incendies, secours à personne nécessitant désincarcération ou intervention sur la voie publique.' },
    { term: 'PLS', def: 'Position Latérale de Sécurité. Position d\'attente pour une victime inconsciente qui respire encore. Empêche l\'asphyxie.' },
    { term: 'Traction axiale', def: 'Technique de retrait du casque consistant à tirer dans l\'axe du corps (sans rotation latérale) en maintenant la nuque alignée.' },
    { term: 'Massage cardiaque', def: '30 compressions thoraciques (5 à 6 cm de profondeur, 100 à 120 par minute) + 2 insufflations. À pratiquer sans interruption si arrêt cardiaque.' },
    { term: 'Garrot', def: 'Lien serré à 5 cm au-dessus d\'une plaie hémorragique impossible à comprimer. À utiliser en dernier recours, noter l\'heure de pose, NE JAMAIS desserrer.' },
    { term: 'Non-assistance', def: 'Délit pénal puni de 5 ans de prison et 75 000 € d\'amende (article 223-6 du Code pénal). La loi protège ceux qui interviennent de bonne foi.' },
    { term: 'Suraccident', def: 'Second accident causé par la non-protection du premier (ex : voiture qui percute les victimes au sol). Évité par le balisage à 30-100 m.' },
  ],

  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur les premiers secours moto tirées de la banque officielle ETM. Tu dois en réussir <strong>au moins 8 sur 10</strong> pour valider ta maîtrise du thème. Ces gestes peuvent un jour sauver une vie — la tienne, ou celle d'un inconnu.",
    themeCode: 'S',
    questionsCount: 10,
    passingScore: 8,
  },

  nextIssue: {
    slug: 'epi',
    number: 7,
    title: 'Casque, gants, blouson : non négociable',
    short: 'Les EPI',
  },
};

export default premiersSecoursEtmIssue;
